import LoadingStatus from "@imminently/decisively-ui/LoadingStatus";
import { EventDetails } from "@/features/events/components/EventDetails";
import { EventIcon } from "@/features/events/components/EventIcon";
import { getEventTypeInfo, normalizeEvent } from "@packages/commons";
import { useHistory } from "react-router-dom";
import { eventDetailsUrl, readEventDetails } from "@/features/events/utils/eventDetails";
import { type Interaction, refireSession, sessionService, tasksService } from "@/app/providers/services";
import { JsonEditor } from "@/common/JsonEditor";
import { useNotify } from "@/common/notifications";
import { Avatar, AvatarGroup } from "@/components";
import { global } from "@/global";
import { useRouting } from "@/hooks/use-routing";
import Button from "@imminently/decisively-ui/Button";
import Icon from "@imminently/decisively-ui/Icon";
import ItemList from "@imminently/decisively-ui/ItemList";
import LoadingSpinner from "@imminently/decisively-ui/LoadingSpinner";
import { useModalContext } from "@imminently/decisively-ui/Modal";
import Select from "@imminently/decisively-ui/Select";
import SplitPane from "@imminently/decisively-ui/SplitPane";
import Tabs from "@imminently/decisively-ui/Tabs";
import Tooltip from "@imminently/decisively-ui/Tooltip";
import { Chip, CircularProgress, Divider, Grid, Typography } from "@mui/material";
import moment from "moment";
import React from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { CallsViewer, getCallData } from "./CallsViewer";
import { InteractionDataView, stringifyJson } from "./InteractionDataView";
import OutboundInteractionDetail from "./OutboundInteractionDetail";

const EVENTS_PAGE_SIZE = 10;

function normalizeEventsPage(raw: any): any[] {
  return Array.isArray(raw)
    ? raw
    : Array.isArray((raw as any)?.data?.data)
      ? (raw as any).data.data
      : Array.isArray((raw as any)?.data)
        ? (raw as any).data
        : [];
}

/** Fetches every page of a session's raw events, in API order (newest first). `isCancelled` stops paging early. */
export async function fetchAllSessionEvents(sessionId: string, isCancelled: () => boolean = () => false): Promise<any[]> {
  const all: any[] = [];
  let cursor: string | undefined;

  while (true) {
    const params = new URLSearchParams();
    params.set("$limit", String(EVENTS_PAGE_SIZE));
    if (cursor) params.set("cursor", cursor);
    const url = `sessions/${encodeURIComponent(sessionId)}/events?${params.toString()}`;
    const res = await global.client.get(url);
    const data = res?.data !== undefined ? res.data : res;
    all.push(...normalizeEventsPage(data));
    cursor = res?.nextCursor ?? res?.data?.nextCursor;
    if (!cursor || isCancelled()) break;
  }

  return all;
}

/** Fetches one interaction with any stored outcome, request and response merged in (the session's `interaction` subresource). */
export async function fetchSessionInteraction(sessionId: string, interactionId: string): Promise<Interaction> {
  const res = await global.client.get(
    `sessions/${encodeURIComponent(sessionId)}/interaction/${encodeURIComponent(interactionId)}`,
  );
  return (res?.data !== undefined ? res.data : res) as Interaction;
}

function toMs(ts: string | undefined): number {
  if (!ts) return Number.NaN;
  const ms = Date.parse(ts);
  return Number.isFinite(ms) ? ms : Number.NaN;
}

interface TimelineItem {
  id: string;
  kind: string;
  label: string;
  mode?: string;
  startMs: number;
  endMs: number;
  isPoint: boolean;
  lane?: number;
  startRowIdx?: number;
  endRowIdx?: number;
  raw: any;
}

const LANE_WIDTH_PX = 280;
const ROW_HEIGHT_PX = 28;

function assignLanesByTimeOverlap(items: TimelineItem[]): { items: TimelineItem[]; laneCount: number } {
  const sorted = [...items].sort((a, b) => {
    if (a.startMs !== b.startMs) return a.startMs - b.startMs;
    return a.endMs - b.endMs;
  });
  const laneEndMs: number[] = [];
  for (const item of sorted) {
    // Reuse a lane only when this item starts strictly after the lane's last item ends (no touch/overlap)
    let lane = 0;
    while (lane < laneEndMs.length && laneEndMs[lane] >= item.startMs) {
      lane += 1;
    }
    if (lane === laneEndMs.length) {
      laneEndMs.push(item.endMs);
    } else {
      laneEndMs[lane] = item.endMs;
    }
    item.lane = lane;
  }
  return { items: sorted, laneCount: Math.max(1, laneEndMs.length) };
}

function buildTimestampRows(items: TimelineItem[]): number[] {
  const timestamps = [...new Set(items.flatMap((i) => [i.startMs, i.endMs]))].filter(Number.isFinite);
  timestamps.sort((a, b) => a - b);
  return timestamps;
}

function findRowRange(rows: number[], startMs: number, endMs: number): { startRowIdx: number; endRowIdx: number } {
  let startRowIdx = 0;
  for (let i = 0; i < rows.length; i += 1) {
    if (rows[i] >= startMs) {
      startRowIdx = i;
      break;
    }
    startRowIdx = i;
  }
  let endRowIdx = startRowIdx;
  for (let i = startRowIdx; i < rows.length; i += 1) {
    if (rows[i] <= endMs) {
      endRowIdx = i;
    } else {
      break;
    }
  }
  return { startRowIdx, endRowIdx };
}

function formatTimeLabel(ms: number): string {
  const d = new Date(ms);
  // Full ISO with milliseconds (space instead of T for readability)
  return d.toISOString().replace("T", " ");
}

function formatDurationMs(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return "—";
  if (ms < 1000) return `${Math.round(ms)}ms`;
  if (ms < 60 * 1000) return `${(ms / 1000).toFixed(1)}s`;
  const mins = Math.floor(ms / 60000);
  const secs = Math.round((ms % 60000) / 1000);
  if (mins < 60) return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
  const hrs = Math.floor(mins / 60);
  const remainMins = mins % 60;
  return remainMins > 0 ? `${hrs}h ${remainMins}m` : `${hrs}h`;
}

function buildEventTimelineItems(events: any[]): TimelineItem[] {
  const items: TimelineItem[] = [];
  const openStartsByKey = new Map<string, any[]>();

  for (const rawEvent of events || []) {
    const eventName = String(rawEvent.eventType ?? rawEvent.messageType ?? rawEvent.event ?? "");
    const ts = normalizeEvent(rawEvent)?.timestamp;
    const tsMs = toMs(ts);
    if (!Number.isFinite(tsMs)) continue;

    const isStart = eventName.endsWith("-start");
    const isEnd = eventName.endsWith("-end");

    if (isStart || isEnd) {
      const base = eventName.replace(/-(start|end)$/, "");
      const key = `${base}::${rawEvent.meta?.requestId ?? rawEvent.requestId ?? rawEvent.eventRowKey ?? "none"}`;
      const current = openStartsByKey.get(key) || [];

      if (isStart) {
        current.push(rawEvent);
        openStartsByKey.set(key, current);
        continue;
      }

      if (current.length === 0) {
        items.push({
          id: `event-point-${rawEvent.id ?? rawEvent.eventRowKey ?? crypto.randomUUID()}`,
          kind: "event-point",
          label: getEventTypeInfo(normalizeEvent(rawEvent)?.eventType)?.name || eventName || "Event",
          startMs: tsMs,
          endMs: tsMs,
          isPoint: true,
          raw: rawEvent
        });
        continue;
      }

      const startEvent = current.pop();
      if (current.length === 0) {
        openStartsByKey.delete(key);
      } else {
        openStartsByKey.set(key, current);
      }

      const startMs = toMs(normalizeEvent(startEvent)?.timestamp);
      const endMs = Math.max(startMs, tsMs);
      items.push({
        id: `event-range-${startEvent.id ?? crypto.randomUUID()}-${rawEvent.id ?? crypto.randomUUID()}`,
        kind: "event-range",
        label: getEventTypeInfo(normalizeEvent(startEvent)?.eventType)?.name || base,
        startMs,
        endMs,
        isPoint: startMs === tsMs,
        raw: { start: startEvent, end: rawEvent }
      });
      continue;
    }

    items.push({
      id: `event-point-${rawEvent.id ?? rawEvent.eventRowKey ?? crypto.randomUUID()}`,
      kind: "event-point",
      label: getEventTypeInfo(normalizeEvent(rawEvent)?.eventType)?.name || eventName || "Event",
      startMs: tsMs,
      endMs: tsMs,
      isPoint: true,
      raw: rawEvent
    });
  }

  for (const [, starts] of openStartsByKey.entries()) {
    for (const startEvent of starts) {
      const startMs = toMs(normalizeEvent(startEvent)?.timestamp);
      items.push({
        id: `event-open-${startEvent.id ?? crypto.randomUUID()}`,
        kind: "event-open-start",
        label: getEventTypeInfo(normalizeEvent(startEvent)?.eventType)?.name || String(startEvent.eventType ?? startEvent.messageType ?? startEvent.event ?? "Event"),
        startMs,
        endMs: startMs,
        isPoint: true,
        raw: startEvent
      });
    }
  }

  return items;
}

function buildInteractionTimelineItems(interactions: any[]): TimelineItem[] {
  const items: TimelineItem[] = [];

  for (const interaction of interactions || []) {
    const createdMs = toMs(interaction.created);
    const lastModifiedMs = toMs(interaction.lastModified ?? interaction.created);

    if (!Number.isFinite(createdMs)) continue;
    const endMs = Number.isFinite(lastModifiedMs) ? Math.max(createdMs, lastModifiedMs) : createdMs;

    const label =
      interaction.comment ??
      interaction.data?.title ??
      interaction.mode ??
      interaction.goal ??
      "interaction";

    items.push({
      id: `interaction-${interaction.id ?? crypto.randomUUID()}`,
      kind: interaction.outbound ? "interaction-outbound" : "interaction-inbound",
      label,
      mode: interaction.mode,
      startMs: createdMs,
      endMs,
      isPoint: createdMs === endMs,
      raw: interaction
    });
  }

  return items;
}

interface TimelineData {
  items: TimelineItem[];
  minMs: number;
  maxMs: number;
}

function buildTimelineData(session: any, events: any[]): TimelineData {
  const interactionItems = buildInteractionTimelineItems(session?.interactions ?? []);
  const eventItems = buildEventTimelineItems(events);
  const all = [...interactionItems, ...eventItems].sort((a, b) => {
    if (a.startMs !== b.startMs) return a.startMs - b.startMs;
    return a.endMs - b.endMs;
  });

  if (all.length === 0) {
    return { items: [], minMs: 0, maxMs: 0 };
  }

  const minMs = Math.min(...all.map((i) => i.startMs));
  const maxMs = Math.max(...all.map((i) => i.endMs));
  const paddedMaxMs = maxMs === minMs ? maxMs + 1 : maxMs;

  return { items: all, minMs, maxMs: paddedMaxMs };
}

function useAllEvents(sessionId: string | undefined) {
  const [events, setEvents] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<Error | null>(null);

  React.useEffect(() => {
    if (!sessionId) {
      setEvents([]);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    const fetchAll = async () => {
      const all = await fetchAllSessionEvents(sessionId, () => cancelled);
      if (!cancelled) {
        setEvents(all);
      }
    };

    fetchAll()
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err : new Error(String(err)));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return { events, loading, error };
}

const MODE_ICONS: Record<string, string> = {
  api: "⚡",
  comms: "✉",
  integration: "⇄",
  task: "☑",
  interview: "📋",
  generative: "🤖",
  storage: "📦",
  entity: "📊"
};

const InputTabTimelineView = ({
  session,
  graph,
}: {
  session: any;
  graph: any;
}) => {
  const history = useHistory();
  const tenancyId = localStorage.getItem("decisively.tenant");
  const { events, loading, error } = useAllEvents(session?.id);
  const [selectedItemId, setSelectedItemId] = React.useState<string | null>(null);

  const timelineData = React.useMemo(
    () => buildTimelineData(session, events),
    [session, events]
  );

  const selectedItem = React.useMemo(
    () => timelineData.items.find((i) => i.id === selectedItemId) ?? null,
    [timelineData.items, selectedItemId]
  );

  const eventIdForFetch = React.useMemo(() => {
    if (!selectedItem?.kind?.startsWith("event") || !selectedItem?.raw) return null;
    const r = selectedItem.raw;
    if (r.id ?? r.eventRowKey) return r.id ?? r.eventRowKey;
    if (r.start?.id ?? r.start?.eventRowKey) return r.start.id ?? r.start.eventRowKey;
    if (r.end?.id ?? r.end?.eventRowKey) return r.end.id ?? r.end.eventRowKey;
    return null;
  }, [selectedItem]);

  const { data: fullEventData, isLoading: loadingEventDetails, error: eventDetailsError } = useQuery(
    ["sessions", "events", session.id, eventIdForFetch, tenancyId],
    () => {
      if (!eventIdForFetch) return Promise.resolve(null);
      return global.client.get(eventDetailsUrl("session", String(eventIdForFetch), session.id)).then(readEventDetails);
    },
    { enabled: Boolean(eventIdForFetch), retry: false }
  );

  const selectedEvent = selectedItem?.kind.startsWith("event")
    ? fullEventData || (!eventIdForFetch ? normalizeEvent(selectedItem.raw?.start || selectedItem.raw) : null)
    : null;

  const tableData = React.useMemo(() => {
    if (timelineData.items.length === 0) {
      return {
        rows: [] as number[],
        items: [] as TimelineItem[],
        laneCount: 1,
        totalHeightPx: 0
      };
    }

    const laneData = assignLanesByTimeOverlap([...timelineData.items]);
    const rows = buildTimestampRows(laneData.items);
    for (const item of laneData.items) {
      const { startRowIdx, endRowIdx } = findRowRange(rows, item.startMs, item.endMs);
      item.startRowIdx = startRowIdx;
      item.endRowIdx = Math.max(startRowIdx, endRowIdx);
    }

    return {
      rows,
      items: laneData.items,
      laneCount: laneData.laneCount,
      totalHeightPx: Math.max(1, rows.length) * ROW_HEIGHT_PX
    };
  }, [timelineData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-4">
        <LoadingSpinner size="sm"  />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-2">
        <Typography variant="body2" color="error">
          Error loading events: {error.message}
        </Typography>
      </div>
    );
  }

  return (
    <SplitPane direction="horizontal" gutterSize={6}>
      <SplitPane.Pane minSize={300} preferredSize="75%">
        <div className="flex h-full flex-col overflow-hidden">
          <div
            className="flex flex-wrap items-center"
            style={{ padding: "12px 16px", gap: 12, flexShrink: 0, borderBottom: "1px solid rgba(0,0,0,0.12)" }}
          >
            <Typography variant="subtitle1">Timeline</Typography>
            <div className="flex items-center" style={{ gap: 12, fontSize: "0.8rem", color: "rgba(0,0,0,0.6)" }}>
              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ width: 12, height: 12, borderRadius: 3, background: "#4fc3f7" }} />
                Inbound
              </span>
              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ width: 12, height: 12, borderRadius: 3, background: "#ff8a65" }} />
                Outbound
              </span>
              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ width: 12, height: 12, borderRadius: 3, background: "#b39ddb" }} />
                Event
              </span>
            </div>
          </div>
          <div
            style={{
              flex: 1,
              minHeight: 0,
              overflow: "auto",
              border: "1px solid rgba(0,0,0,0.12)",
              borderRadius: 4,
              background: "#fafafa"
            } as React.CSSProperties}
          >
            {timelineData.items.length === 0 ? (
              <div className="flex items-center justify-center p-4">
                <Typography variant="body2" color="textSecondary">
                  No interactions or events for this session.
                </Typography>
              </div>
            ) : null}
            {tableData.rows.length > 0 ? (
              <div
                className="flex"
                style={{
                  minHeight: tableData.totalHeightPx,
                  width: Math.max(400, 11 * 16 + tableData.laneCount * LANE_WIDTH_PX)
                }}
              >
                <div
                  className="shrink-0"
                  style={{
                    width: "11em",
                    display: "grid",
                    gridTemplateRows: `repeat(${Math.max(1, tableData.rows.length)}, ${ROW_HEIGHT_PX}px)`,
                    borderRight: "1px solid rgba(0,0,0,0.12)",
                    background: "#f5f5f5"
                  }}
                >
                  {tableData.rows.map((timeMs) => (
                    <div
                      key={timeMs}
                      style={{
                        borderBottom: "1px solid rgba(0,0,0,0.12)",
                        fontSize: "0.75rem",
                        color: "rgba(0,0,0,0.65)",
                        padding: "2px 6px 0 0",
                        textAlign: "right",
                        whiteSpace: "nowrap",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end"
                      }}
                    >
                      {formatTimeLabel(timeMs)}
                    </div>
                  ))}
                </div>
                <div
                  className="flex-1"
                  style={{
                    minWidth: tableData.laneCount * LANE_WIDTH_PX,
                    display: "grid",
                    alignItems: "stretch",
                    gridTemplateColumns: `repeat(${Math.max(1, tableData.laneCount)}, ${LANE_WIDTH_PX}px)`,
                    gridTemplateRows: `repeat(${Math.max(1, tableData.rows.length)}, ${ROW_HEIGHT_PX}px)`,
                    backgroundImage:
                      "linear-gradient(to bottom, rgba(0,0,0,0.12) 1px, transparent 1px), linear-gradient(to right, rgba(0,0,0,0.12) 1px, transparent 1px)",
                    backgroundSize: `100% ${ROW_HEIGHT_PX}px, ${LANE_WIDTH_PX}px 100%`
                  }}
                >
                  {tableData.items.map((item) => {
                    const isSelected = selectedItemId === item.id;
                    const bgColor = item.kind === "interaction-inbound"
                      ? "#4fc3f7"
                      : item.kind === "interaction-outbound"
                        ? "#ff8a65"
                        : "#b39ddb";
                    const timelineEvent = item.kind.startsWith("event") ? normalizeEvent(item.raw?.start || item.raw) : null;
                    const eventInfo = getEventTypeInfo(timelineEvent?.eventType);
                    const modeIcon = item.mode ? MODE_ICONS[item.mode] ?? item.mode : null;
                    const startRowIdx = item.startRowIdx ?? 0;
                    const endRowIdx = item.endRowIdx ?? startRowIdx;
                    const spanRows = Math.max(1, endRowIdx - startRowIdx + 1);

                    return (
                      <Tooltip key={item.id}>
                        <Tooltip.Trigger asChild>
                      <button
                        type="button"
                        onClick={() => setSelectedItemId(item.id)}
                        aria-label={item.label}
                        className={timelineEvent && isSelected ? "ring-2 ring-[#7e57c2]" : ""}
                        style={{
                          gridColumn: `${(item.lane ?? 0) + 1}`,
                          gridRow: `${startRowIdx + 1} / span ${spanRows}`,
                          border: "none",
                          borderRadius: 2,
                          margin: "2px 3px",
                          padding: "2px 10px",
                          textAlign: "left",
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                          background: bgColor,
                          color: "#0d0d0d",
                          fontSize: "0.8rem",
                          fontWeight: 600,
                          cursor: "pointer",
                          outline: isSelected && !timelineEvent ? "2px solid #ffee58" : "none",
                          zIndex: isSelected ? 3 : 1,
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                          gap: 6
                        }}
                      >
                        <span
                          style={{
                            flex: 1,
                            minWidth: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "flex",
                            alignItems: "center",
                            gap: 4
                          }}
                        >
                          {timelineEvent && <EventIcon eventType={timelineEvent.eventType} size={16} className="shrink-0" />}
                          <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
                            {item.label}
                          </span>
                          {modeIcon ? (
                            <span style={{ flexShrink: 0, fontSize: "0.75rem" }} title={item.mode}>
                              {modeIcon}
                            </span>
                          ) : null}
                        </span>
                        {(item.endMs - item.startMs) > 0 ? (
                          <span
                            style={{
                              flexShrink: 0,
                              fontSize: "0.75rem",
                              opacity: 0.9
                            }}
                          >
                            {formatDurationMs(item.endMs - item.startMs)}
                          </span>
                        ) : null}
                      </button>
                        </Tooltip.Trigger>
                        <Tooltip.Content placement="bottom">
                          <div className="max-w-sm space-y-1">
                            <div className="font-medium">{item.label}</div>
                            {timelineEvent && <code className="block break-all text-xs">{timelineEvent.eventType}</code>}
                            {eventInfo?.description && <p className="text-sm">{eventInfo.description}</p>}
                            {timelineEvent?.message && <p className="text-sm">{timelineEvent.message}</p>}
                            <p className="text-xs">{formatTimeLabel(item.startMs)}{!item.isPoint && ` → ${formatTimeLabel(item.endMs)}`}</p>
                          </div>
                        </Tooltip.Content>
                      </Tooltip>
                    );
                  })}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </SplitPane.Pane>
      <SplitPane.Pane minSize={320} preferredSize="40%" className="min-h-0 overflow-hidden">
        <div className={`flex h-full min-h-0 flex-col ${selectedItem?.kind.startsWith("event") ? "overflow-hidden" : "overflow-auto p-3"}`}>
          {!selectedItem ? (
            <p className="text-sm text-textSecondary">Select an interaction or event to view its details.</p>
          ) : selectedItem.kind.startsWith("event") ? (
            loadingEventDetails ? <LoadingStatus className="h-full" role="status" message="Loading event details…" />
              : eventDetailsError ? <p role="alert" className="p-3 text-sm text-destructive">Unable to load event details: {(eventDetailsError as Error).message}</p>
              : selectedEvent ? <EventDetails
                  event={{ ...selectedEvent, sessionId: selectedEvent.sessionId || session.id }}
                />
              : <p className="p-3 text-sm text-textSecondary">No event details available.</p>
          ) : (
            <>
              <h3 className="mb-2 text-sm font-medium text-textSecondary">Interaction data</h3>
              <JsonEditor value={JSON.stringify(selectedItem.raw, null, 2)} disabled withCopy />
            </>
          )}
        </div>
      </SplitPane.Pane>
    </SplitPane>
  );
};

const COMMENT_TRUNCATE_LEN = 20;

function truncateComment(comment: string | undefined, mode?: string): string {
  if (!comment) return mode ? `(${mode})` : "(—)";
  return comment.length <= COMMENT_TRUNCATE_LEN
    ? comment
    : comment.slice(0, COMMENT_TRUNCATE_LEN) + "…";
}

function getModeIcon(mode: string | undefined): React.ReactNode {
  switch (mode) {
    case "interview":
      return <Icon name="show-data" />;
    case "task":
      return <Icon name="flag" />;
    case "api":
      return <Icon name="code" />;
    case "generative":
      return <Icon name="message" />;
    case "integration":
      return <Icon name="code" />;
    case "comms":
      return <Icon name="email" />;
    case "storage":
      return <Icon name="view-chart" />;
    case "entity":
      return <Icon name="view-chart" />;
    default:
      return <Icon name="settings" />;
  }
}

const TASK_CANCELLATION_REASON_EXPLANATIONS: Record<string, string> = {
  CHILD_NODE_CHANGED:
    "This task was cancelled because something the trigger depends on changed, so the trigger condition is no longer true. " +
    "Check the trigger's rule and the attributes it uses: if the user can change those answers (e.g. in another screen or flow), " +
    "the task may be cancelled when they do. If that's not what you want, make the trigger depend only on inputs that won't " +
    "change after the task is created, or adjust the flow so the user completes the task before changing those answers.",
  NOT_RELEVANT:
    "This task was cancelled because the attribute is no longer considered relevant to this goal (e.g. the user moved to a " +
    "different path or the relevancy rules excluded this goal), and the outcome hadn't been set yet. If you expect this task " +
    "to stay open, check how relevancy is defined for this goal and whether the path the user is on still includes it.",
  VALUE_SET_ELSEWHERE:
    "This task was cancelled because the outcome for this goal was already set (e.g. answered in another question or by " +
    "another part of the flow), so the task is no longer needed. If you didn't intend that, check where else that outcome can " +
    "be set—including other screens, branches, or APIs—and either remove that path or change the flow so the task is the " +
    "only (or primary) place that sets it.",
};

const InputTab = ({
  session,
  graph,
  release,
  selectInteractionId = null,
  onSelectInteractionHandled,
}: {
  session: any;
  graph: any;
  release?: any;
  selectInteractionId?: string | null;
  onSelectInteractionHandled?: () => void;
}) => {
  const { t } = useTranslation();
  const notify = useNotify();
  const [selectedInteraction, setSelectedInteraction] = React.useState<string | null>(null);
  const [refiring, setRefiring] = React.useState(false);
  const { goToPage } = useRouting();
  const modalContext = useModalContext();

  // When parent requests selecting an interaction (e.g. from history modal "go to interaction"), select it once
  React.useEffect(() => {
    if (selectInteractionId) {
      setSelectedInteraction(selectInteractionId);
      onSelectInteractionHandled?.();
    }
  }, [selectInteractionId, onSelectInteractionHandled]);
  const [sortBy, setSortBy] = React.useState<'lastModified' | 'created'>(() => {
    if (typeof localStorage !== 'undefined') {
      return (localStorage.getItem('input-tab-sort-by') as any) || 'created';
    }
    return 'created';
  });

  React.useEffect(() => {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('input-tab-sort-by', sortBy);
    }
  }, [sortBy]);

  // Sort interactions by selected field in descending order
  const sortedInteractions = React.useMemo(() => {
    if (!session?.interactions) {
      return [];
    }
    return [...session.interactions].sort((a, b) => {
      const timeA = a[sortBy] ? new Date(a[sortBy]).getTime() : 0;
      const timeB = b[sortBy] ? new Date(b[sortBy]).getTime() : 0;
      return timeB - timeA;
    });
  }, [session?.interactions, sortBy]);

  const selectedInteractionData = React.useMemo(() => {
    if (selectedInteraction === null) {
      return null;
    }
    return sortedInteractions.find(i => i.id === selectedInteraction) || null;
  }, [selectedInteraction, sortedInteractions]);

  // Find associated activation for scheduled interactions
  const associatedActivation = React.useMemo(() => {
    if (!selectedInteractionData?.scheduled || !session?.scheduled?.activations) {
      return null;
    }
    return session.scheduled.activations.find(
      (activation: any) => activation?.trigger?.payload?.interaction === selectedInteractionData.id
    ) || null;
  }, [selectedInteractionData?.id, selectedInteractionData?.scheduled, session?.scheduled?.activations]);

  // For outbound interactions with stored data (outcomeFile or hasStoredData), fetch full interaction via session subresource (merged from storage)
  const interactionService = sessionService.useSubresource<Interaction>("interaction", session?.id ?? "");
  const shouldFetchFullInteraction = Boolean(
    selectedInteractionData?.outbound &&
    (selectedInteractionData?.outcomeFile || selectedInteractionData?.hasStoredData) &&
    session?.id &&
    selectedInteractionData?.id
  );
  const { data: fullInteraction, isLoading: isLoadingFullInteraction } = interactionService.useGetOne(
    selectedInteractionData?.id ?? "",
    { enabled: shouldFetchFullInteraction }
  );
  const displayInteraction = React.useMemo(() => {
    if (!selectedInteractionData) {
      return null;
    }
    if (selectedInteractionData.outbound && fullInteraction) {
      return fullInteraction;
    }
    return selectedInteractionData;
  }, [selectedInteractionData, fullInteraction]);
  const selectedInteractionDisplayData = React.useMemo(() => {
    if (!selectedInteractionData) {
      return session?.data || {};
    }
    const interaction = displayInteraction ?? selectedInteractionData;
    return interaction.calls ?? interaction.payload ?? interaction.data ?? {};
  }, [displayInteraction, selectedInteractionData, session?.data]);
  const selectedInteractionCalls = React.useMemo(() => {
    if (!selectedInteractionData) {
      return null;
    }
    const interaction = displayInteraction ?? selectedInteractionData;
    return Array.isArray(interaction.calls) ? interaction.calls : null;
  }, [displayInteraction, selectedInteractionData]);
  const selectedInteractionDebugData = React.useMemo(() => {
    if (selectedInteractionCalls?.length) {
      return getCallData(selectedInteractionCalls[selectedInteractionCalls.length - 1]);
    }
    return selectedInteractionDisplayData ?? {};
  }, [selectedInteractionCalls, selectedInteractionDisplayData]);
  const selectedInteractionDataTabLabel = React.useMemo(() => {
    if (!selectedInteractionData) {
      return "Data";
    }
    if (selectedInteractionCalls) return "Calls";
    const interaction = displayInteraction ?? selectedInteractionData;
    return interaction.payload !== undefined ? "Payload" : "Data";
  }, [displayInteraction, selectedInteractionData, selectedInteractionCalls]);
  const refireSessionVersion = React.useMemo(() => {
    const interactionScopedVersion = (displayInteraction ?? selectedInteractionData)?.data?.sessionVersion;
    if (interactionScopedVersion !== undefined && interactionScopedVersion !== null && String(interactionScopedVersion).trim() !== "") {
      return interactionScopedVersion;
    }
    return session?.version;
  }, [displayInteraction, selectedInteractionData, session?.version]);

  const selectedData = React.useMemo(() => {
    if (selectedInteraction === null) {
      return session?.data || {};
    }

    // For task mode, show meta instead of data (data won't exist)
    if (selectedInteractionData?.mode === "task") {
      // If scheduled, show the trigger from the associated activation
      if (associatedActivation?.trigger) {
        return associatedActivation.trigger;
      }
      // Otherwise show meta
      return selectedInteractionData?.meta || {};
    }

    return selectedInteractionDisplayData || {};
  }, [selectedInteraction, session?.data, selectedInteractionData, associatedActivation, selectedInteractionDisplayData]);

  // Get goal node description from graph
  const goalDescription = React.useMemo(() => {
    if (!selectedInteractionData?.goal || !graph) {
      return null;
    }
    try {
      const node = graph.node?.(selectedInteractionData.goal);
      if (node && node.description) {
        return node.description;
      }
      // Fallback to GUID if node not found or has no description
      return selectedInteractionData.goal;
    } catch {
      return selectedInteractionData.goal;
    }
  }, [selectedInteractionData?.goal, graph]);

  // Determine if we should fetch task data
  const shouldFetchTask = React.useMemo(() => {
    if (selectedInteractionData?.mode !== 'task') {
      return false;
    }
    // If no scheduled time, fetch task
    if (!selectedInteractionData.scheduled) {
      return true;
    }
    // If scheduled time is in the past, fetch task
    const scheduledTime = new Date(selectedInteractionData.scheduled).getTime();
    const now = Date.now();
    return scheduledTime <= now;
  }, [selectedInteractionData?.mode, selectedInteractionData?.scheduled]);

  // Fetch task data if conditions are met
  // Only fetch when we have a task interaction and it should be fetched
  const shouldFetch = shouldFetchTask && !!selectedInteractionData?.id && selectedInteractionData?.mode === 'task';
  const interactionId = selectedInteractionData?.id;

  // Memoize the filter to ensure it's stable and included in the query
  const taskFilter = React.useMemo(() => {
    if (shouldFetch && interactionId) {
      return {
        interaction: interactionId,
      };
    }
    return undefined;
  }, [shouldFetch, interactionId]);

  const { data: taskData, isLoading: isLoadingTask } = tasksService.useGetList({
    meta: taskFilter,
    enabled: shouldFetch && !!interactionId,
  });

  // Get the first task from the result (should only be one)
  // Also verify it matches the current interaction to prevent showing stale data
  const task = React.useMemo(() => {
    // Don't show task if we're not on a task interaction or shouldn't fetch
    if (!shouldFetch || !selectedInteractionData || selectedInteractionData.mode !== 'task') {
      return null;
    }
    if (!taskData?.data || taskData.data.length === 0) {
      return null;
    }
    const foundTask = taskData.data[0];
    // Verify the task matches the current interaction
    if (foundTask.interaction !== selectedInteractionData.id) {
      return null;
    }
    return foundTask;
  }, [taskData, selectedInteractionData, shouldFetch]);

  const [activeTab, setActiveTab] = React.useState<string>('data');

  // Reset tab to 'data' when interaction changes
  React.useEffect(() => {
    setActiveTab('data');
  }, [selectedInteraction]);

  const handleDebug = (data: any, event: React.MouseEvent) => {
    event.stopPropagation();
    goToPage('/build/debugger', {
      goal: session?.goal,
      data
    });
    modalContext?.close();
  };

  const [viewMode, setViewMode] = React.useState<"list" | "timeline">("list");

  return (
    <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as "list" | "timeline")} className="flex flex-col h-full">
      <Tabs.List className="px-4 pt-2 border-b border-border flex-shrink-0">
        <Tabs.Trigger value="list">
          <Icon name="show-data" style={{ marginRight: 6 }} />
          List
        </Tabs.Trigger>
        <Tabs.Trigger value="timeline">
          <Icon name="timeline" style={{ marginRight: 6 }} />
          Timeline
        </Tabs.Trigger>
      </Tabs.List>
      <Tabs.Content value="list" className="flex-1 min-h-0 overflow-hidden">
        <SplitPane direction="horizontal" gutterSize={6}>
          <SplitPane.Pane minSize={200} preferredSize="300px">
            <div className="h-full flex flex-col">
              <div className="px-3 py-2 border-b border-border flex items-center gap-2 flex-shrink-0">
                <Typography variant="caption" color="textSecondary" style={{ whiteSpace: 'nowrap' }}>Sort by:</Typography>
                <Select
                  value={sortBy}
                  onValueChange={(v) => setSortBy(v as any)}
                  options={[
                    { value: 'lastModified', label: 'Modified' },
                    { value: 'created', label: 'Created' },
                  ]}
                  className="flex-1"
                />
              </div>
              <ItemList
                className="h-full"
                items={[{ id: "__all__" }, ...sortedInteractions]}
                getItemKey={(interaction: any) => interaction.id}
                renderItem={(interaction: any) => {
                  if (interaction.id === "__all__") {
                    return (
                      <ItemList.Item
                        key="__all__"
                        selected={selectedInteraction === null}
                        onClick={() => setSelectedInteraction(null)}
                      >
                        <ItemList.Left>
                          <ItemList.Title>All</ItemList.Title>
                          <div>Session Data</div>
                        </ItemList.Left>
                        <ItemList.Right>
                          <Tooltip>
                            <Tooltip.Trigger>
                              <Button icon label="Run in debugger" onClick={(e) => handleDebug(session?.data || {}, e)}>
                                <Icon name="error" />
                              </Button>
                            </Tooltip.Trigger>
                            <Tooltip.Content>{t("Run in debugger")}</Tooltip.Content>
                          </Tooltip>
                        </ItemList.Right>
                      </ItemList.Item>
                    );
                  }

                  const status = interaction.status ?? "—";
                  const comment = interaction.comment;
                  const commentDisplay = truncateComment(comment, interaction.mode || "Interaction");
                  const commentFull = comment || (interaction.mode || "Interaction");
                  const scheduledTime =
                    interaction.scheduled ??
                    session?.scheduled?.activations?.find(
                      (a: any) => a?.trigger?.payload?.interaction === interaction.id,
                    )?.activateAt;

                  return (
                    <ItemList.Item
                      key={interaction.id}
                      selected={selectedInteraction === interaction.id}
                      onClick={() => setSelectedInteraction(interaction.id)}
                    >
                      <ItemList.Left>
                        <ItemList.Title>
                          <Tooltip>
                            <Tooltip.Trigger>
                              <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                                {commentDisplay}
                              </span>
                            </Tooltip.Trigger>
                            <Tooltip.Content>{comment ? commentFull : (interaction.mode || "Interaction")}</Tooltip.Content>
                          </Tooltip>
                        </ItemList.Title>
                        <div style={{ color: "#666", fontSize: "0.85em" }}>
                          {interaction.created
                            ? moment(interaction.created).format("YYYY-MM-DD HH:mm")
                            : moment(interaction[sortBy] || interaction.lastModified).fromNow()}
                          {scheduledTime ? (
                            <>
                              {" · "}
                              Scheduled: {moment(scheduledTime).fromNow()}
                            </>
                          ) : null}
                        </div>
                      </ItemList.Left>
                      <ItemList.Right>
                        {interaction.outbound ? (
                          <Tooltip>
                            <Tooltip.Trigger>
                              <span style={{ display: "inline-flex", alignItems: "center", marginRight: 4 }}>
                                <Icon name="open-in-new" style={{ color: "#666" }} />
                              </span>
                            </Tooltip.Trigger>
                            <Tooltip.Content>Outbound</Tooltip.Content>
                          </Tooltip>
                        ) : null}
                        <Tooltip>
                          <Tooltip.Trigger>
                            <span style={{ display: "inline-flex", alignItems: "center", marginRight: 4 }}>
                              {getModeIcon(interaction.mode)}
                            </span>
                          </Tooltip.Trigger>
                          <Tooltip.Content>{interaction.mode || "Interaction"}</Tooltip.Content>
                        </Tooltip>
                        <Chip
                          label={status}
                          size="small"
                          color={
                            status === "complete" ? "primary" : status === "error" ? "secondary" : "default"
                          }
                          style={{ fontSize: "0.7rem", height: 20 }}
                        />
                      </ItemList.Right>
                    </ItemList.Item>
                  );
                }}
              />
            </div>
          </SplitPane.Pane>
          <SplitPane.Pane>
            {selectedInteractionData ? (
              <SplitPane direction="horizontal" gutterSize={6}>
                {/* Metadata Column - use displayInteraction so outbound with outcomeFile shows resolved data */}
                <SplitPane.Pane minSize={300} preferredSize="400px">
                  <div className="h-full flex flex-col overflow-hidden">
                    <div className="flex-1 overflow-auto p-4">
                      <div className="flex items-center justify-between gap-2" style={{ marginBottom: 16 }}>
                        <Typography variant="h6">Interaction Metadata</Typography>
                        <div className="flex items-center gap-1">
                          {(displayInteraction ?? selectedInteractionData)?.outbound &&
                            (displayInteraction ?? selectedInteractionData)?.status === "error" && (
                              <Button
                                size="sm"
                                variant="contained" intent="secondary"
                                disabled={refiring || !session?.id || !selectedInteractionData?.id}
                                onClick={async () => {
                                  if (!session?.id || !selectedInteractionData?.id) return;
                                  setRefiring(true);
                                  try {
                                    await refireSession(session.id, selectedInteractionData.id, refireSessionVersion);
                                    notify.success("Refire sent");
                                  } catch (err: any) {
                                    notify.error(err?.message ?? "Refire failed");
                                  } finally {
                                    setRefiring(false);
                                  }
                                }}
                                label={refiring ? "Refiring…" : "Refire"}
                              >
                                {refiring ? "Refiring…" : "Refire"}
                              </Button>
                            )}
                          <Tooltip>
                            <Tooltip.Trigger>
                              <Button icon label="Run in debugger" onClick={(e) => handleDebug(selectedInteractionDebugData, e)}>
                                <Icon name="error" />
                              </Button>
                            </Tooltip.Trigger>
                            <Tooltip.Content>{t("Run in debugger")}</Tooltip.Content>
                          </Tooltip>
                        </div>
                      </div>
                      {/* First row: Comment (truncated with tooltip) — Type, Outbound, Status in metadata detail */}
                      <div className="grid grid-cols-12 gap-2">
                        {selectedInteractionData?.comment && (
                          <>
                            <div className="col-span-3">
                              <Typography variant="caption" color="textSecondary">Comment</Typography>
                            </div>
                            <div className="col-span-9">
                              <Tooltip>
                                <Tooltip.Trigger>
                                  <Typography variant="body2" style={{ wordBreak: "break-word" }}>
                                    {selectedInteractionData.comment}
                                  </Typography>
                                </Tooltip.Trigger>
                                <Tooltip.Content>{selectedInteractionData.comment}</Tooltip.Content>
                              </Tooltip>
                            </div>
                          </>
                        )}
                        <div className="col-span-3">
                          <Typography variant="caption" color="textSecondary">Type</Typography>
                        </div>
                        <div className="col-span-3">
                          <Typography variant="body2" style={{ fontWeight: 500 }}>
                            {(displayInteraction ?? selectedInteractionData).mode || "N/A"}
                          </Typography>
                        </div>
                        {displayInteraction?.outbound && (
                          <>
                            <div className="col-span-3">
                              <Typography variant="caption" color="textSecondary">Outbound</Typography>
                            </div>
                            <div className="col-span-3">
                              <Chip icon={<Icon name="open-in-new" />} label="Outbound" size="small" />
                            </div>
                          </>
                        )}
                        <div className="col-span-3">
                          <Typography variant="caption" color="textSecondary">Status</Typography>
                        </div>
                        <div className="col-span-3">
                          <Chip
                            label={(displayInteraction ?? selectedInteractionData).status || "N/A"}
                            size="small"
                            color={
                              (displayInteraction ?? selectedInteractionData).status === "complete"
                                ? "primary"
                                : (displayInteraction ?? selectedInteractionData).status === "error"
                                  ? "secondary"
                                  : "default"
                            }
                          />
                        </div>
                        {/* Second row: Created · Scheduled (if present) */}
                        <div className="col-span-3">
                          <Typography variant="caption" color="textSecondary">Created</Typography>
                        </div>
                        <div className="col-span-3">
                          <Typography variant="body2">
                            {selectedInteractionData.created
                              ? moment(selectedInteractionData.created).format("YYYY-MM-DD HH:mm:ss")
                              : "N/A"}
                          </Typography>
                        </div>
                        {selectedInteractionData.scheduled && (
                          <>
                            <div className="col-span-3">
                              <Typography variant="caption" color="textSecondary">Scheduled</Typography>
                            </div>
                            <div className="col-span-3">
                              <Typography variant="body2">
                                {moment(selectedInteractionData.scheduled).format("YYYY-MM-DD HH:mm:ss")}
                                {" "}
                                <span style={{ color: "#666", fontSize: "0.9em" }}>
                                  ({moment(selectedInteractionData.scheduled).fromNow()})
                                </span>
                              </Typography>
                            </div>
                          </>
                        )}
                        {selectedInteractionData?.mode === 'task' &&
                          selectedInteractionData?.status === 'cancelled' &&
                          selectedInteractionData?.reasonCode ? (
                          <>
                            <div className="col-span-12">
                              <Divider style={{ marginTop: 8, marginBottom: 8 }} />
                            </div>
                            <div className="col-span-3">
                              <Typography variant="caption" color="textSecondary">Cancellation reason</Typography>
                            </div>
                            <div className="col-span-9">
                              <Typography variant="body2" component="span" style={{ fontWeight: 500 }}>
                                {selectedInteractionData.reasonCode}
                              </Typography>
                            </div>
                            <div className="col-span-12">
                              <Typography variant="body2" color="textSecondary" style={{ whiteSpace: "pre-wrap" }}>
                                {TASK_CANCELLATION_REASON_EXPLANATIONS[selectedInteractionData.reasonCode] ??
                                  "No explanation available for this reason code."}
                              </Typography>
                            </div>
                          </>
                        ) : null}
                        <div className="col-span-3">
                          <Typography variant="caption" color="textSecondary">Last Modified</Typography>
                        </div>
                        <div className="col-span-3">
                          <Typography variant="body2">
                            {selectedInteractionData.lastModified
                              ? moment(selectedInteractionData.lastModified).format('YYYY-MM-DD HH:mm:ss')
                              : 'N/A'}
                          </Typography>
                        </div>
                        {selectedInteractionData.createdBy && (
                          <>
                            <div className="col-span-3">
                              <Typography variant="caption" color="textSecondary">Created By</Typography>
                            </div>
                            <div className="col-span-3">
                              <Typography variant="body2">
                                {selectedInteractionData.createdBy.full_name ||
                                  selectedInteractionData.createdBy.identity_id ||
                                  'N/A'}
                                {selectedInteractionData.createdBy.is_app_key && (
                                  <Chip
                                    label="App Key"
                                    size="small"
                                    style={{ marginLeft: 8, height: 20, fontSize: '0.7rem' }}
                                  />
                                )}
                              </Typography>
                            </div>
                          </>
                        )}
                        {selectedInteractionData.lastModifiedBy && (
                          <>
                            <div className="col-span-3">
                              <Typography variant="caption" color="textSecondary">Last Modified By</Typography>
                            </div>
                            <div className="col-span-3">
                              <Typography variant="body2">
                                {selectedInteractionData.lastModifiedBy.full_name ||
                                  selectedInteractionData.lastModifiedBy.identity_id ||
                                  'N/A'}
                                {selectedInteractionData.lastModifiedBy.is_app_key && (
                                  <Chip
                                    label="App Key"
                                    size="small"
                                    style={{ marginLeft: 8, height: 20, fontSize: '0.7rem' }}
                                  />
                                )}
                              </Typography>
                            </div>
                          </>
                        )}
                        {goalDescription && (
                          <>
                            <div className="col-span-12">
                              <Divider style={{ marginTop: 8, marginBottom: 8 }} />
                            </div>
                            <div className="col-span-3">
                              <Typography variant="caption" color="textSecondary">Goal</Typography>
                            </div>
                            <div className="col-span-9">
                              <Typography variant="body2">{goalDescription}</Typography>
                            </div>
                          </>
                        )}
                        {(selectedInteractionData.mode === 'task' || selectedInteractionData.mode === 'interview') &&
                          selectedInteractionData.interviewName && (
                            <>
                              <div className="col-span-3">
                                <Typography variant="caption" color="textSecondary">Interview Name</Typography>
                              </div>
                              <div className="col-span-9">
                                <Typography variant="body2">{selectedInteractionData.interviewName}</Typography>
                              </div>
                            </>
                          )}
                        {selectedInteractionData.comment && (
                          <>
                            <div className="col-span-12">
                              <Divider style={{ marginTop: 8, marginBottom: 8 }} />
                            </div>
                            <div className="col-span-3">
                              <Typography variant="caption" color="textSecondary">Comment</Typography>
                            </div>
                            <div className="col-span-9">
                              <Typography variant="body2" style={{ wordBreak: 'break-word' }}>
                                {selectedInteractionData.comment}
                              </Typography>
                            </div>
                          </>
                        )}
                        {selectedInteractionData?.mode === 'task' && task && (((task?.assigneesData && task.assigneesData.length > 0) || (task?.assignees && task.assignees.length > 0)) || task?.escalation?.nextEscalationTime) ? (
                          <>
                            <div className="col-span-12">
                              <Divider style={{ marginTop: 8, marginBottom: 8 }} />
                            </div>
                            {(task?.assigneesData && task.assigneesData.length > 0) || (task?.assignees && task.assignees.length > 0) ? (
                              <>
                                <div className="col-span-3">
                                  <Typography variant="caption" color="textSecondary">Assignees</Typography>
                                </div>
                                <div className="col-span-9">
                                  <Tooltip>
                                    <Tooltip.Trigger>
                                      <AvatarGroup size="sm" max={5}>
                                        {task.assigneesData && task.assigneesData.length > 0
                                          ? task.assigneesData.map((assignee) => {
                                            const fullName = assignee.name || assignee.full_name;
                                            const initials = fullName
                                              ? fullName.split(' ').map(n => n[0]).join('').toUpperCase()
                                              : (assignee.email || assignee.identity_id || '?').split('@')[0].substring(0, 2).toUpperCase();

                                            return (
                                              <Avatar
                                                key={assignee.id || assignee.identity_id}
                                                name={fullName || assignee.identity_id || assignee.email}
                                                alt={assignee.name || assignee.full_name || assignee.identity_id || assignee.email}
                                                size="sm"
                                              >
                                                {initials}
                                              </Avatar>
                                            );
                                          })
                                          : task.assignees?.map((assigneeId: string) => (
                                            <Avatar
                                              key={assigneeId}
                                              size="sm"
                                            >
                                              {assigneeId.includes('@')
                                                ? assigneeId.split('@')[0].substring(0, 2).toUpperCase()
                                                : assigneeId.substring(0, 2).toUpperCase()}
                                            </Avatar>
                                          ))}
                                      </AvatarGroup>
                                    </Tooltip.Trigger>
                                    <Tooltip.Content>
                                      {task.assigneesData && task.assigneesData.length > 0
                                        ? task.assigneesData.map(a => a.name || a.full_name || a.identity_id || a.email).join(', ')
                                        : task.assignees?.join(', ')}
                                    </Tooltip.Content>
                                  </Tooltip>
                                </div>
                              </>
                            ) : null}
                            {task?.escalation?.nextEscalationTime && (
                              <>
                                <div className="col-span-3">
                                  <Typography variant="caption" color="textSecondary">Next Escalation</Typography>
                                </div>
                                <div className="col-span-9">
                                  <Typography variant="body2">
                                    {moment(task.escalation.nextEscalationTime).format('YYYY-MM-DD HH:mm:ss')}
                                    {' '}
                                    <span style={{ color: '#666', fontSize: '0.9em' }}>
                                      ({moment(task.escalation.nextEscalationTime).fromNow()})
                                    </span>
                                  </Typography>
                                </div>
                              </>
                            )}
                          </>
                        ) : null}
                        {selectedInteractionData?.mode === 'task' && shouldFetchTask && isLoadingTask && (
                          <div className="col-span-12">
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                              <LoadingSpinner size="sm"  />
                              <Typography variant="caption" color="textSecondary">
                                Loading task data...
                              </Typography>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </SplitPane.Pane>

                {/* Tabbed Data Column */}
                <SplitPane.Pane>
                  <div className="h-full flex flex-col overflow-hidden">
                    {selectedInteractionData?.outbound ? (
                      shouldFetchFullInteraction && isLoadingFullInteraction ? (
                        <div className="flex-1 flex items-center justify-center p-4">
                          <LoadingSpinner size="sm"  />
                        </div>
                      ) : (
                        <OutboundInteractionDetail
                      interaction={displayInteraction ?? selectedInteractionData}
                      sessionId={session?.id}
                    />
                      )
                    ) : selectedInteractionData?.mode === "task" ? (
                      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col h-full">
                        <Tabs.List className="px-4 pt-4 border-b border-border">
                          <Tabs.Trigger value="meta">Meta</Tabs.Trigger>
                          <Tabs.Trigger value="data">{selectedInteractionDataTabLabel}</Tabs.Trigger>
                          <Tabs.Trigger value="task">Task</Tabs.Trigger>
                        </Tabs.List>
                        <Tabs.Content value="meta" className="flex-1 overflow-auto p-4">
                          <JsonEditor
                            value={stringifyJson(selectedData)}
                            disabled
                            withCopy
                          />
                        </Tabs.Content>
                        <Tabs.Content value="data" className="flex-1 overflow-auto p-0">
                          {selectedInteractionCalls ? (
                            <div className="p-4">
                              <CallsViewer calls={selectedInteractionCalls} graph={graph} release={release} />
                            </div>
                          ) : (
                            <InteractionDataView
                              data={selectedInteractionDisplayData ?? {}}
                              graph={graph}
                            />
                          )}
                        </Tabs.Content>
                        <Tabs.Content value="task" className="flex-1 overflow-auto p-4">
                          {isLoadingTask ? (
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                              <LoadingSpinner size="sm"  />
                            </div>
                          ) : task ? (
                            <JsonEditor
                              value={JSON.stringify(task, null, 2)}
                              disabled
                              withCopy
                            />
                          ) : (
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                              <Typography variant="body2" color="textSecondary">
                                {shouldFetchTask
                                  ? 'No task found for this interaction'
                                  : selectedInteractionData.scheduled
                                    ? `Task scheduled for ${moment(selectedInteractionData.scheduled).format('YYYY-MM-DD HH:mm:ss')}`
                                    : 'Task data not available'}
                              </Typography>
                            </div>
                          )}
                        </Tabs.Content>
                      </Tabs>
                    ) : (
                      <div className="flex-1 overflow-auto">
                        {selectedInteractionCalls ? (
                          <CallsViewer calls={selectedInteractionCalls} graph={graph} release={release} />
                        ) : (
                          <InteractionDataView
                            data={selectedData}
                            graph={graph}
                          />
                        )}
                      </div>
                    )}
                  </div>
                </SplitPane.Pane>
              </SplitPane>
            ) : (
              // "All" tab - show selected data without the metadata panel
              <div className="h-full flex flex-col overflow-hidden">
                <div className="flex-1 overflow-auto">
                  <InteractionDataView
                    data={selectedData}
                    graph={graph}
                  />
                </div>
              </div>
            )}
          </SplitPane.Pane>
        </SplitPane>
      </Tabs.Content>
      <Tabs.Content value="timeline" className="flex-1 min-h-0 overflow-hidden">
        <InputTabTimelineView session={session} graph={graph} />
      </Tabs.Content>
    </Tabs>
  );
};

export default InputTab;
